import React, { useState, useEffect, useRef } from 'react';
import Live2DPreview from './Live2DPreview';
import avatarAssets from '../utils/avatarAssets';
import { authAPI } from '../utils/apiUtils';
import { trackEvent } from '../firebase/analytics';
import { ANALYTICS_EVENTS } from '../firebase/analyticsEvents';
import './AvatarSelection.css';

const AvatarSelection = ({ isOpen, onClose, onComplete, userInfo }) => {
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [avatarData, setAvatarData] = useState([]);
  const live2dPreviewRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadAvatarData();
    }
  }, [isOpen]);

  const loadAvatarData = async () => {
    try {
      // Get Live2D avatars directly
      const live2dAvatars = avatarAssets.getLive2dAvatars();
      
      console.log('[AvatarSelection] Available Live2D avatars:', live2dAvatars);
      console.log('[AvatarSelection] User info:', userInfo);
      
      setAvatarData(live2dAvatars);
      
      // Check for user's existing avatar selection
      let currentAvatarId = null;
      
      // Check userInfo first
      if (userInfo?.avatarId) {
        currentAvatarId = userInfo.avatarId;
        console.log('[AvatarSelection] Found avatarId in userInfo:', currentAvatarId);
      } else if (userInfo?.selectedAvatar?.id) {
        currentAvatarId = userInfo.selectedAvatar.id;
        console.log('[AvatarSelection] Found selectedAvatar.id in userInfo:', currentAvatarId);
      } else {
        // Check localStorage as fallback
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);
            currentAvatarId = parsedUser.avatarId || parsedUser.selectedAvatar?.id;
            console.log('[AvatarSelection] Found avatarId in localStorage:', currentAvatarId);
          } catch (error) {
            console.warn('[AvatarSelection] Error parsing saved user:', error);
          }
        }
      }
      
      // Find and select the user's current avatar
      if (currentAvatarId && live2dAvatars.length > 0) {
        const currentAvatar = live2dAvatars.find(avatar => avatar.id === currentAvatarId);
        if (currentAvatar) {
          setSelectedAvatar(currentAvatar);
          console.log('[AvatarSelection] Selected user\'s current avatar:', currentAvatar);
          
          // Scroll to the selected avatar after a short delay
          setTimeout(() => {
            scrollToSelectedAvatar(currentAvatar.id);
          }, 100);
        } else {
          console.warn('[AvatarSelection] User\'s avatar not found in available avatars, selecting first avatar');
          setSelectedAvatar(live2dAvatars[0]);
        }
      } else if (live2dAvatars.length > 0) {
        // No current avatar found, select random avatar for first-time users
        const randomIndex = Math.floor(Math.random() * live2dAvatars.length);
        const randomAvatar = live2dAvatars[randomIndex];
        setSelectedAvatar(randomAvatar);
        console.log('[AvatarSelection] No current avatar found, selected random avatar:', randomAvatar);
        
        // Scroll to the randomly selected avatar
        setTimeout(() => {
          scrollToSelectedAvatar(randomAvatar.id);
        }, 100);
      }
    } catch (error) {
      console.error('[AvatarSelection] Error loading avatar data:', error);
    }
  };

  const scrollToSelectedAvatar = (avatarId) => {
    const avatarElement = document.querySelector(`[data-avatar-id="${avatarId}"]`);
    if (avatarElement) {
      avatarElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'center'
      });
    }
  };

  const handleAvatarSelect = (avatar) => {
    console.log('[AvatarSelection] Avatar selected:', avatar);
    setSelectedAvatar(avatar);
    
    // Scroll to selected avatar
    setTimeout(() => {
      scrollToSelectedAvatar(avatar.id);
    }, 100);
  };

  const handleBack = () => {
    // Cleanup camera before going back
    if (live2dPreviewRef.current) {
      live2dPreviewRef.current.cleanup();
    }
    onClose();
  };

  const handleNext = async () => {
    if (!selectedAvatar) {
      alert('Please select an avatar first.');
      return;
    }

    setIsLoading(true);

    try {
      console.log('[AvatarSelection] Saving avatar selection:', selectedAvatar);
      
      // Cleanup camera before proceeding
      if (live2dPreviewRef.current) {
        live2dPreviewRef.current.cleanup();
      }

      // Prepare user info with selected avatar
      const updatedUserInfo = {
        ...userInfo,
        avatarId: selectedAvatar.id,
        selectedAvatar: selectedAvatar,
        avatarName: selectedAvatar.name,
        avatarImage: selectedAvatar.src,
        live2dPath: selectedAvatar.live2dPath
      };

      // Save avatar selection to backend
      const response = await authAPI.updateProfile({
        avatarId: selectedAvatar.id,
        avatarName: selectedAvatar.name,
        avatarImage: selectedAvatar.src,
        live2dPath: selectedAvatar.live2dPath
      });

      console.log('[AvatarSelection] Backend response:', response);

      // Merge response data with user info
      const finalUserInfo = {
        ...updatedUserInfo,
        ...(response.user && response.user) // Merge any additional user data from response
      };
      
      // Update localStorage with the selected avatar
      const existingUser = JSON.parse(localStorage.getItem('user') || '{}');
      const finalUserInfoWithExisting = { ...existingUser, ...finalUserInfo };
      localStorage.setItem('user', JSON.stringify(finalUserInfoWithExisting));
      
      onComplete(finalUserInfoWithExisting);
    } catch (error) {
      console.error('Error saving avatar selection:', error);
      if (error.message.includes('401')) {
        alert('Your session has expired. Please log in again.');
        // Could trigger a logout here if needed
      } else {
        alert('Failed to save avatar selection. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderAvatarGrid = () => {
    return (
      <div className="avatar-grid">
        {avatarData.map((avatar) => (
          <div
            key={avatar.id}
            className={`avatar-option ${selectedAvatar?.id === avatar.id ? 'selected' : ''}`}
            onClick={() => handleAvatarSelect(avatar)}
            data-avatar-id={avatar.id}
          >
            <img src={avatarAssets.getFullAssetUrl(avatar.src)} alt={avatar.name} />
            <div className="avatar-overlay">
              {selectedAvatar?.id === avatar.id && (
                <div className="selected-indicator">✓</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="avatar-selection-overlay">
      <div className="avatar-selection-container">
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Saving your avatar...</p>
          </div>
        )}
        
        {/* Floating Next Button */}
        <button 
          className="floating-next-button"
          onClick={handleNext}
          disabled={!selectedAvatar || isLoading}
        >
          NEXT
        </button>
        
        {/* Floating Title */}
        <h1 className="floating-title">Select your Avatar</h1>
        
        {/* Main Content Area */}
        <div className="main-content-area">
          {/* Left side - Avatar grid */}
          <div className="avatar-grid-section">
            <div className="avatar-grid-container">
              {renderAvatarGrid()}
            </div>
          </div>
          
          {/* Right side - Live2D Preview */}
          <div className="avatar-preview-section">
            <div className="avatar-preview-container">
              {selectedAvatar ? (
                <Live2DPreview 
                  ref={live2dPreviewRef}
                  live2dPath={avatarAssets.getFullAssetUrl(selectedAvatar.live2dPath)}
                  width="100%"
                  height="100%"
                />
              ) : (
                <div className="placeholder-avatar">
                  <div className="placeholder-icon">🎭</div>
                  <p className="placeholder-text">Select a Live2D avatar</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvatarSelection;
